import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  LinearProgress,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  TrendingUp as ProductivityIcon,
  Message as MessageIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { TeamManagementService, TeamMemberWithStats } from '../../services/TeamManagementService';

// TeamMember interface now imported from TeamManagementService

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`team-tabpanel-${index}`}
      aria-labelledby={`team-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * Team Management Component for Project Manager
 * Manage team members, assignments, and performance
 */
const TeamManagement: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [teamMembers, setTeamMembers] = useState<TeamMemberWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMemberWithStats | null>(null);

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Load real team data from API
      const members = await TeamManagementService.getAllTeamMembersWithStats();
      setTeamMembers(members);
    } catch (err) {
      setError('Failed to load team data. Please try again.');
      console.error('Error loading team data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, member: TeamMemberWithStats) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  const handleSendMessage = async () => {
    if (selectedMember) {
      try {
        // In a real implementation, open a message dialog
        alert(`Send message to ${selectedMember.name}`);
        // await TeamManagementService.sendMessage(selectedMember.id, 'Subject', 'Message');
      } catch (err) {
        console.error('Error sending message:', err);
        setError('Failed to send message');
      }
    }
    handleMenuClose();
  };

  const handleViewTasks = () => {
    if (selectedMember) {
      // Navigate to tasks view or open tasks dialog
      alert(`View tasks for ${selectedMember.name}`);
    }
    handleMenuClose();
  };

  const handleViewProfile = () => {
    if (selectedMember) {
      // Navigate to member profile
      alert(`View profile for ${selectedMember.name}`);
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'away': return 'warning';
      case 'offline': return 'error';
      default: return 'default';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'online': return '#4caf50';
      case 'away': return '#ff9800';
      case 'offline': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const formatLastActive = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  const TeamOverview = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5">Team Overview</Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          Add Team Member
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 4 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <PersonIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">{teamMembers.length}</Typography>
                <Typography variant="body2" color="text.secondary">Total Team Members</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <TaskIcon color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">
                  {teamMembers.reduce((sum, member) => sum + member.activeTasks, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Active Tasks</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <ProductivityIcon color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">
                  {Math.round(teamMembers.reduce((sum, member) => sum + member.productivity, 0) / teamMembers.length)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Avg Productivity</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge color="success" variant="dot">
                <PersonIcon color="action" sx={{ fontSize: 40 }} />
              </Badge>
              <Box>
                <Typography variant="h4">
                  {teamMembers.filter(member => member.status === 'online').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">Online Now</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Team Members</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Active Tasks</TableCell>
                  <TableCell>Productivity</TableCell>
                  <TableCell>Current Project</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: getStatusBadgeColor(member.status),
                                border: 2,
                                borderColor: 'background.paper',
                              }}
                            />
                          }
                        >
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                        </Badge>
                        <Box>
                          <Typography variant="subtitle2">{member.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {member.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{member.role}</TableCell>
                    <TableCell>
                      <Chip 
                        label={member.status} 
                        size="small" 
                        color={getStatusColor(member.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Badge badgeContent={member.activeTasks} color="primary">
                        <TaskIcon />
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress 
                          variant="determinate" 
                          value={member.productivity} 
                          sx={{ width: 60, height: 6 }}
                        />
                        <Typography variant="body2">{member.productivity}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {member.currentProject || 'Not assigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, member)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Team Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadTeamData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Team Overview" />
          <Tab label="Task Assignment" />
          <Tab label="Performance Review" />
          <Tab label="Time Tracking" />
        </Tabs>
      </Box>

      <TabPanel value={currentTab} index={0}>
        <TeamOverview />
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <Box textAlign="center" py={4}>
          <TaskIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Task Assignment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Task assignment functionality is coming soon!
          </Typography>
          <Button variant="outlined">
            Request Feature
          </Button>
        </Box>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <Box textAlign="center" py={4}>
          <ProductivityIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Performance Review
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Performance review tools are coming soon!
          </Typography>
          <Button variant="outlined">
            Request Feature
          </Button>
        </Box>
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        <Box textAlign="center" py={4}>
          <ScheduleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Time Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Time tracking features are coming soon!
          </Typography>
          <Button variant="outlined">
            Request Feature
          </Button>
        </Box>
      </TabPanel>

      {/* Member Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewProfile}>
          <PersonIcon sx={{ mr: 1 }} fontSize="small" />
          View Profile
        </MenuItem>
        <MenuItem onClick={handleViewTasks}>
          <TaskIcon sx={{ mr: 1 }} fontSize="small" />
          View Tasks
        </MenuItem>
        <MenuItem onClick={handleSendMessage}>
          <MessageIcon sx={{ mr: 1 }} fontSize="small" />
          Send Message
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TeamManagement;